// Profile media upload
//
// The single place that puts a user's photo/video bytes into Supabase Storage
// and hands back a public URL. Extracted from PhotoManager so the onboarding
// PhotosStep uploads through exactly the same path — onboarding previously
// only made a local `URL.createObjectURL` blob URL, which meant photos added
// during onboarding never reached the backend at all and vanished on reload.

import { supabase } from '../../lib/supabase';

const STORAGE_BUCKET = 'profile-media';

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];

export const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
export const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB

// Maps a validated MIME type to the storage file extension. The uploaded
// file's own name is untrustworthy for this (missing extension, unusual
// casing, multiple dots, etc.) and the backend's photo_url CHECK constraint
// only recognizes a specific set of extensions — deriving from the already
// client-validated MIME type guarantees a match regardless of file naming.
export const MIME_TO_EXTENSION: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
};

export async function uploadToStorage(
  userId: string,
  file: File | Blob,
  extension: string,
  contentType: string
): Promise<string> {
  const path = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${extension}`;
  const { error } = await supabase.storage.from(STORAGE_BUCKET).upload(path, file, { contentType });
  if (error) throw error;
  const { data } = supabase.storage.from(STORAGE_BUCKET).getPublicUrl(path);
  return data.publicUrl;
}

/**
 * Validates an image file and uploads it, returning the hosted public URL.
 * Throws with a user-presentable message on validation failure.
 */
export async function uploadProfileImage(userId: string, file: File): Promise<string> {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    throw new Error('Please choose a JPG, PNG, or WebP image.');
  }
  if (file.size > MAX_IMAGE_SIZE) {
    throw new Error('Images must be under 10MB.');
  }
  const extension = MIME_TO_EXTENSION[file.type];
  return uploadToStorage(userId, file, extension, file.type);
}
